import { createContext, useContext, useMemo, useState } from "react";
import { apiRequest } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { toastService } from "@miplatform/toast";
import { Sha256Hash } from "@miplatform/utils";
import { ManagerApprovalModal } from "../../views/Schedule/Scheduler/ManagerApprovalModal/ManagerApprovalModal";

const GlobalManagerApprovalContext = createContext();

export const GlobalManagerApprovalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const [requestedPermission, setRequestedPermission] = useState(null);
  const [resourceGroup, setResourceGroup] = useState(null);

  const [requestOptions, setRequestOptions] = useState(null);
  const [callbackRequest, setCallbackRequest] = useState(null);
  const [abortCallbackRequest, setAbortCallbackRequest] = useState(null);
  const [modalDescription, setModalDescription] = useState("");
  const [confirmBtnLabel, setConfirmBtnLabel] = useState("");

  const executeApiRequest = async ({
    options,
    callback,
    abortCallback,
    toastMessageForManagerApproval,
    successToastMessageForManagerApproval,
    noInitialErrorMessage = false,
    modalDescription,
    confirmBtnLabel,
  }) => {
    const response = await apiRequest(options);

    if (response.status === HTTP_STATUSES.MANAGER_APPROVAL_REQUIRE) {
      const requestedPermission =
        response.headers["x-mip-manager-override-permission"];

      if (!requestedPermission) {
        toastService.error({
          text: "You don't have the permission to perform this action.",
        });
        return response;
      }

      if (toastMessageForManagerApproval) {
        toastService.error(toastMessageForManagerApproval);
      }

      if (!toastMessageForManagerApproval && !noInitialErrorMessage) {
        toastService.error({
          text: "Manager Approval Failed",
          subtext:
            "The provided username or PIN is incorrect. Please verify your credentials and try again.",
        });
      }

      setIsOpen(true);
      setRequestOptions(options);
      setModalDescription(modalDescription);
      setConfirmBtnLabel(confirmBtnLabel);

      if (callback) {
        setCallbackRequest(() => callback);
      }
      if (abortCallback) {
        setAbortCallbackRequest(() => abortCallback);
      }

      setRequestedPermission(requestedPermission);
      setResourceGroup(response.headers["x-mip-manager-override-reason-group"]);

      return response;
    }

    if (callback) {
      if (successToastMessageForManagerApproval && response.status < 300) {
        toastService.success(successToastMessageForManagerApproval);
      }
      callback(response);
    }

    if (callbackRequest) {
      callbackRequest(response);
      setCallbackRequest(null);
      toastService.success({
        text: "Manager Approval Granted",
        subtext:
          "Manager approval has been received. You may now proceed with this action.",
      });
      if (successToastMessageForManagerApproval && response.status < 300) {
        toastService.success(successToastMessageForManagerApproval);
      }
    }

    setIsOpen(false);
    setModalDescription("");
    setConfirmBtnLabel("");

    return response;
  };

  const exec = async ({ selectedUserId, userPin, reason }) => {
    const ph = await Sha256Hash(userPin);
    return executeApiRequest({
      options: {
        ...requestOptions,
        headers: {
          ...requestOptions?.headers,
          "X-MIP-MANAGER-ID": selectedUserId,
          "X-MIP-MANAGER-PIN": ph,
          "X-MIP-MANAGER-REASON": reason,
        },
      },
    });
  };

  const contextValue = useMemo(
    () => ({
      executeApiRequest,
    }),
    [callbackRequest, isOpen]
  );

  return (
    <GlobalManagerApprovalContext.Provider value={contextValue}>
      <ManagerApprovalModal
        open={isOpen}
        closeModal={() => {
          setIsOpen(false);
          setCallbackRequest(null);
          setRequestOptions(null);
          setRequestedPermission(null);
          setResourceGroup(null);
          setModalDescription("");
          setConfirmBtnLabel("");
          if (abortCallbackRequest) {
            abortCallbackRequest();
            setAbortCallbackRequest(null);
          }
        }}
        resourceGroup={resourceGroup}
        requestedPermission={requestedPermission}
        execute={exec}
        modalDescription={modalDescription}
        confirmBtnLabel={confirmBtnLabel}
      />
      {children}
    </GlobalManagerApprovalContext.Provider>
  );
};

export const useGlobalManagerApproval = () => {
  const context = useContext(GlobalManagerApprovalContext);
  if (context === undefined) {
    throw new Error(
      "useGlobalManagerApproval must be used within a GlobalManagerApprovalProvider"
    );
  }

  return context;
};
